import { useApi } from '@hero/context';
import type {
  UseCurrentUserApi,
  UseCanUseAppApi,
  UseLogoutApi,
  UseChangeLanguageApi,
  UseSwitchOrgApi,
  UseDeleteAccountApi,
  UseSendOtpApi,
  UseSignInApi,
  UseSignUpApi,
} from './types';
import { useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get the current user's identity: `{ user, org?, driver? }` (auth-v2
 * unified shape — `org` is the full Org doc for the active org).
 * Uses `api.auth.me()` from whichever SDK the app provides via `<ApiProvider>`.
 *
 * Disabled until a real access token is present — avoids spamming
 * `/auth/me` with 401s on the login screen or after logout.
 *
 * Hook API: {@link UseCurrentUserApi}
 */
export const useCurrentUserApi: UseCurrentUserApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'currentUser',
    apiFn: api.auth.me,
    query: undefined,
    apiConfig: { disableIfNotAuth: true, ...apiConfig },
  });
};

/**
 * Poll the `/auth/can-use-app` endpoint. Returns
 * `{ status, message }`. The web `useCanUseAppGuard` consumes this
 * to redirect blocked users out of the app.
 *
 * Hook API: {@link UseCanUseAppApi}
 */
export const useCanUseAppApi: UseCanUseAppApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'currentUser',
    apiFn: api.auth.canUseApp,
    query: undefined,
    apiConfig: { disableIfNotAuth: true, ...apiConfig },
  });
};

/**
 * Sign out the current user. API from ApiProvider; pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseLogoutApi}
 */
export const useLogoutApi: UseLogoutApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.logout,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};

/**
 * Change the current user's language. Returns new access_token + user.
 * API from ApiProvider; pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseChangeLanguageApi}
 */
export const useChangeLanguageApi: UseChangeLanguageApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.changeLanguage,
    apiConfig,
  });
};

/**
 * Switch to a different organization. Returns new tokens + user.
 * API from ApiProvider; pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseSwitchOrgApi}
 */
export const useSwitchOrgApi: UseSwitchOrgApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.switchOrg,
    apiConfig,
  });
};

/**
 * Delete the current user's account. Returns boolean.
 * API from ApiProvider; pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseDeleteAccountApi}
 */
export const useDeleteAccountApi: UseDeleteAccountApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.deleteAccount,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};

/**
 * Send OTP to a phone number. Shared endpoint for both carrier and customer.
 * API from ApiProvider; pass apiConfig to override apiFn, onSuccess, etc.
 *
 * Hook API: {@link UseSendOtpApi}
 */
export const useSendOtpApi: UseSendOtpApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.sendOTP,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};

/**
 * Unified sign-in (relations-v2). One hook for every user type
 * (customer, carrier, driver, broker). Resolves to the unified
 * `auth.signIn` SDK method which is shared between
 * `customer-sdk` and `carrier-sdk`. Pick `customer` or `carrier` as
 * the SDK source via `apiConfig.api`, defaults to `customer`.
 *
 * Hook API: {@link UseSignInApi}
 */
export const useSignInApi: UseSignInApi = apiConfig => {
  const api = useApi('customer');
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.signIn,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};

/**
 * Unified sign-up (relations-v2). Discriminated by the wizard's
 * `personType`. Backend creates `Org` as primary and reverse-mirrors
 * to legacy `Business`/`Carrier`. See
 * `docs/relations-v2/08-user-identity.md`.
 *
 * Hook API: {@link UseSignUpApi}
 */
export const useSignUpApi: UseSignUpApi = apiConfig => {
  const api = useApi('customer');
  return useMutationAPI({
    entity: 'currentUser',
    apiFn: api.auth.signUp,
    apiConfig,
    defaultInvalidationStrategy: 'none',
  });
};
